/**
 * The numbers behind a collection page, worked out once and away from the markup.
 *
 * WHY THIS IS A PLAIN MODULE. Two renderers feed this page — the corpora insight payload for a
 * corpus-backed collection and the storage feed for a standalone one — and the page they produce is
 * now the same page. Keeping the arithmetic here means the server can assemble it either way and
 * the client component only has to draw. It also keeps the one rule that matters testable in
 * isolation: nothing in here invents a figure. Every function returns null or an empty list when
 * the payload has nothing real to put in it, and the component drops the element rather than
 * printing a dash where a claim belongs.
 *
 * THE ONE THING TO WATCH. Facet totals and the collection total are DIFFERENT POPULATIONS. A facet
 * covers only the records that declare that field, so a share is of the declared set, never of the
 * collection — dividing by the collection total mixes the two and produces percentages that
 * silently fail to sum. Every share below is computed against its own dimension's total, and each
 * dimension carries the sentence that says so.
 */

import type { ExplorerDimension } from "@/lib/collection-dimensions";

export type Seg = {
  label: string;
  /** Printed inside the band only when the whole word fits; the legend carries every value anyway. */
  shortLabel: string;
  countFmt: string;
  pct: string;
  /** CSS width, a percentage of the dimension's own total. */
  width: string;
  /** Ink ramp step, 0-based. The component turns this into a colour. */
  step: number;
  labelFits: boolean;
};

export type Dim = {
  id: string;
  /** The dimension's own noun — "Frameworks", "Document type". The rail labels itself from this
      rather than choosing between two hardcoded words and calling frameworks "areas". */
  title: string;
  eyebrow: string;
  /** "59,075 of 86,000 declared · 68.7%" — the scope of the bands, stated with them. */
  declaredMono: string;
  scope: string;
  partition: boolean;
  segs: Seg[];
};

export type RailCell = { k: string; v: string };

export type Cadence = {
  every: string | null;
  last: string | null;
  added: string | null;
  what: string;
};

export type FieldRow = {
  name: string;
  /** Only where a schema is published for the collection. Never guessed from the field's name. */
  type?: string;
  /** Records carrying the field. Only where the payload can say; the column disappears otherwise. */
  present?: number;
  note?: string;
};

export const fmtInt = (n: number) => n.toLocaleString("en-US");

/** Short form for the headline figure, which sits at 68px and must not wrap. */
export function fmtShort(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e4) return `${Math.round(n / 1e3)}k`;
  return n.toLocaleString("en-US");
}

const pct1 = (x: number) => `${(Math.round(x * 10) / 10).toFixed(1)}%`;

/**
 * The growth panel's two paths, from the real series.
 *
 * A SHAPE, NOT A CHART — no axis and no endpoint value, because the series carries absolute counts
 * and this page publishes ratios rather than the totals behind them. Returns null under four
 * points, where a line says nothing a reader could rely on.
 */
export function growthPaths(
  series: { t: number; v: number }[] | undefined | null,
  w = 360,
  h = 130,
): { line: string; area: string; samples: number } | null {
  const pts = (series ?? []).filter((d) => typeof d?.v === "number");
  if (pts.length < 4) return null;

  const vs = pts.map((d) => d.v);
  const lo = Math.min(...vs);
  const hi = Math.max(...vs);
  const span = hi - lo || 1;
  const x = (i: number) => (i / (pts.length - 1)) * w;
  const y = (v: number) => h - ((v - lo) / span) * h;

  const line = pts.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(d.v).toFixed(1)}`).join(" ");
  return { line, area: `${line} L${w} ${h} L0 ${h} Z`, samples: pts.length };
}

/**
 * Bands for one dimension.
 *
 * Long tails are folded into a single "Other" band rather than drawn as a row of slivers: below a
 * few percent a band is thinner than its own border and the legend is doing the work anyway. An
 * ordered dimension (a year axis) keeps every band, because the shape across time IS the point and
 * folding its tail would flatten the story it tells.
 */
export function toDim(d: ExplorerDimension, collectionTotal: number | null): Dim | null {
  const rows = d.rows.filter((r) => r.count > 0);
  if (rows.length < 2) return null;

  const total = rows.reduce((n, r) => n + r.count, 0);
  if (total <= 0) return null;

  const ordered = d.id === "year";
  let banded = rows;
  if (!ordered && rows.length > 6) {
    const head = rows.slice(0, 5);
    const rest = total - head.reduce((n, r) => n + r.count, 0);
    if (rest > 0) {
      /* Named for what it IS, not "Other" — the facets contain a genuine value called "Other",
         and a legend reading "Other 519" beside "Other (3 more) 618" gives the reader no way to
         tell which row is the fold. */
      const n = rows.length - 5;
      banded = [...head, { label: `Remaining ${n} ${n === 1 ? "value" : "values"}`, count: rest }];
    }
  }

  /* The strip is about 1024px of content at the design's width. 10px mono caps with tracking run
     roughly 8.4px a character, and the band carries 22px of padding. A label that would be clipped
     is not printed at all — a half-word inside a band reads as a rendering fault. */
  const segs: Seg[] = banded.map((r, i) => {
    const share = (r.count / total) * 100;
    const room = (share / 100) * 1024 - 22;
    const fits = r.label.length * 8.4 <= room;
    return {
      label: r.label,
      shortLabel: fits ? r.label : "",
      countFmt: fmtInt(r.count),
      pct: pct1(share),
      width: `${share.toFixed(2)}%`,
      step: i,
      labelFits: fits,
    };
  });

  const declaredMono =
    collectionTotal && collectionTotal > 0
      ? d.partition
        ? `${fmtInt(total)} of ${fmtInt(collectionTotal)} shelved · ${pct1((total / collectionTotal) * 100)}`
        : `${fmtInt(total)} of ${fmtInt(collectionTotal)} declared · ${pct1((total / collectionTotal) * 100)}`
      : `${fmtInt(total)} counted`;

  return {
    id: d.id,
    title: d.title,
    eyebrow: d.partition ? `${d.title} · partition` : `${d.title} · declared`,
    declaredMono,
    scope: d.scope,
    partition: Boolean(d.partition),
    segs,
  };
}

/**
 * How often the collection is written to, from the crawl cycle the payload reports.
 *
 * `last_cycle` is 0 on eight of the twenty-seven collections — unset rather than "the epoch" — so a
 * falsy value drops the row instead of printing 1 January 1970, which is the shape of bug that
 * makes a live page look abandoned.
 */
export function cadenceFrom(
  growth: { cycle_s?: number; last_cycle?: number; added_24h?: number } | undefined | null,
  unit: string,
): Cadence | null {
  if (!growth) return null;

  const cycle = growth.cycle_s;
  let every: string | null = null;
  if (typeof cycle === "number" && cycle > 0) {
    if (cycle < 90) every = "Continuously";
    else if (cycle < 5400) every = `Every ${Math.round(cycle / 60)} minutes`;
    else if (cycle < 86400) every = `Every ${Math.round(cycle / 3600)} hours`;
    else every = `Every ${Math.round(cycle / 86400)} days`;
  }

  const lastMs = typeof growth.last_cycle === "number" && growth.last_cycle > 0
    ? growth.last_cycle * 1000
    : null;
  const last = lastMs
    ? new Date(lastMs).toLocaleString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", timeZone: "UTC",
      }) + " UTC"
    : null;

  const added = typeof growth.added_24h === "number" && growth.added_24h > 0
    ? `${fmtInt(growth.added_24h)} in the last day`
    : null;

  if (!every && !last && !added) return null;

  const singular = unit.replace(/s$/, "");
  return {
    every, last, added,
    /* "A new record is a newly published record." is what the generic form produces when the unit
       IS "records", which is a sentence that says nothing. Name the thing only when naming it adds
       something. */
    what:
      (singular === "record"
        ? "A new record is something newly published."
        : `A new record is a newly published ${singular}.`) +
      " A revision replaces the text on the record that already exists rather than adding a second one, so the count does not double.",
  };
}

/** The stat rail: what the collection is made of, in the order a reader asks for it. */
export function railFrom(input: {
  dims: Dim[];
  yearSpan?: string | null;
  fieldCount?: number | null;
  perRecord?: string | null;
}): RailCell[] {
  const out: RailCell[] = [];
  for (const d of input.dims) {
    if (d.id === "shelves") { out.push({ k: "shelves", v: String(d.segs.length) }); continue; }
    if (d.id === "year" && input.yearSpan) { out.push({ k: "years covered", v: input.yearSpan }); continue; }
    /* The dimension's own noun. Hardcoding "areas" labelled a split by framework "areas declared". */
    out.push({ k: `${d.title.toLowerCase()} declared`, v: String(d.segs.length) });
  }
  if (input.fieldCount) out.push({ k: "fields per record", v: String(input.fieldCount) });
  if (input.perRecord) out.push({ k: "per record", v: input.perRecord });
  return out;
}

/**
 * The sentence under the bands, for whichever band the reader last pressed.
 *
 * A partition and a facet mean different things and the difference is the whole reason this line
 * exists: shelf shares add to the collection, facet shares add only to the records that declared
 * the field. Saying so once, next to the number, is what stops the bands being read as a pie.
 */
export function readoutFor(d: Dim, i: number, unit: string): { readout: string; note: string } | null {
  const sg = d.segs[i] ?? d.segs[0];
  if (!sg) return null;

  if (d.partition) {
    return {
      readout: `${sg.countFmt} ${unit} sit on the ${sg.label} shelf — ${sg.pct} of the collection.`,
      note: `Shelves partition the collection: every ${unit.replace(/s$/, "")} is on exactly one, so these shares add to the whole.`,
    };
  }
  return {
    readout: `${sg.countFmt} ${unit} are filed under ${sg.label} — ${sg.pct} of those that declare it.`,
    note: `${d.declaredMono}. The rest carry no value for this field, so these shares are of the declared set and do not add up to the collection.`,
  };
}
